// Meeting notes v2, the notes banner and processing stages (C-14).
//
// Namespace `meetings-notes`; owner WP-5 (BUILD 1). Only the owner adds keys or
// translations. Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  overview: 'Summary',
  decisions: 'Decisions',
  actionItems: 'Action items',
  openQuestions: 'Open questions',
  risks: 'Risks',
  ownerInferred: 'Owner: {name} (inferred)',
  due: 'Due {date}',
  dueText: 'Due: {text}',
  dueInferred: 'Due {date} (inferred from “{text}”)',
  notesGenerating: 'Preparing notes…',
  notesStale: 'The transcript or speakers changed. Refreshing the notes…',
  notesCurrent: 'Notes are up to date.',
  notesFailed: 'The notes could not be prepared.',
  notesLegacyFailed: 'The transcript is safe, but automatic notes need another try.',
  notesNone: 'No notes yet.',
  retryNotes: 'Retry notes',
  regenerateNotes: 'Regenerate notes',
  notesRetryFailed: 'The notes could not be retried.',
  transcriptRows: 'Transcript {rows}',
  showEvidence: 'Show transcript evidence',
  coveragePartial:
    'Notes cover {included} of {total} transcript segments. Review the remaining transcript directly.',
  suggestionsEyebrow: 'Review before saving',
  suggestionsTitle: 'Possible company knowledge updates',
  suggestionsBody:
    'These are suggestions from the meeting. Nothing is added to company knowledge automatically.',
  reviewInChat: 'Review in chat',
  reviewInChatPrompt:
    'Check this point from the meeting against our knowledge pages and, if it is lasting and accurate, suggest an update to the right page for review: “{text}”',
  relatedContext: 'Related company context',
  stageCapturing: 'Recording',
  stageSealing: 'Saving the recording',
  stageTranscribing: 'Transcribing the recording',
  stageProcessing: 'Preparing the transcript',
  stageReady: 'Ready',
  rowNotesGenerating: 'Ready · preparing notes',
  rowNotesStale: 'Ready · refreshing notes',
  rowNotesFailed: 'Ready · notes need attention',
  rowFailed: 'Needs attention',
  processingEyebrow: 'Meeting processing',
  processingNote:
    'You can close this canvas. Processing continues in the background and the original remains available.',
  stageFailedSealing: 'The recording could not be saved. The audio is kept.',
  stageFailedTranscribing: 'The recording could not be transcribed. The original is kept.',
  stageFailedOther: 'The original recording is safe, but processing could not finish.',
  failedEyebrow: 'Meeting needs attention',
  retry: 'Try again',
  retryFailed: 'The meeting could not be retried.',
  canvasEyebrow: 'Meeting · Original retained',
  noAttendees: 'No attendees added',
  paymentRequired:
    "Your company's usage credit is used up. An administrator can add a card, then try again.",
  failureDetails: 'Details',
  retryOwnerOnly: 'The person who added this meeting can try processing again.',
};

export type MeetingsNotesCopyKey = keyof typeof en;
type Copy = Record<MeetingsNotesCopyKey, string>;

const sv: Copy = {
  overview: 'Sammanfattning',
  decisions: 'Beslut',
  actionItems: 'Åtgärder',
  openQuestions: 'Öppna frågor',
  risks: 'Risker',
  ownerInferred: 'Ansvarig: {name} (härledd)',
  due: 'Senast {date}',
  dueText: 'Senast: {text}',
  dueInferred: 'Senast {date} (härlett från ”{text}”)',
  notesGenerating: 'Förbereder anteckningar…',
  notesStale: 'Transkriptet eller talarna ändrades. Anteckningarna uppdateras…',
  notesCurrent: 'Anteckningarna är aktuella.',
  notesFailed: 'Anteckningarna kunde inte tas fram.',
  notesLegacyFailed:
    'Transkriptet är sparat, men de automatiska anteckningarna behöver ett nytt försök.',
  notesNone: 'Inga anteckningar än.',
  retryNotes: 'Försök igen med anteckningarna',
  regenerateNotes: 'Skapa anteckningarna på nytt',
  notesRetryFailed: 'Anteckningarna kunde inte startas om.',
  transcriptRows: 'Transkript {rows}',
  showEvidence: 'Visa stöd i transkriptet',
  coveragePartial:
    'Anteckningarna täcker {included} av {total} transkriptsegment. Läs resten av transkriptet direkt.',
  suggestionsEyebrow: 'Granska innan du sparar',
  suggestionsTitle: 'Möjliga uppdateringar av företagskunskapen',
  suggestionsBody:
    'Det här är förslag från mötet. Inget läggs till i företagskunskapen automatiskt.',
  reviewInChat: 'Granska i chatten',
  reviewInChatPrompt:
    'Jämför den här punkten från mötet med våra kunskapssidor och föreslå, om den är varaktig och korrekt, en uppdatering av rätt sida för granskning: ”{text}”',
  relatedContext: 'Relaterad företagskontext',
  stageCapturing: 'Spelar in',
  stageSealing: 'Sparar inspelningen',
  stageTranscribing: 'Transkriberar inspelningen',
  stageProcessing: 'Förbereder transkriptet',
  stageReady: 'Klar',
  rowNotesGenerating: 'Klar · förbereder anteckningar',
  rowNotesStale: 'Klar · uppdaterar anteckningar',
  rowNotesFailed: 'Klar · anteckningarna behöver åtgärdas',
  rowFailed: 'Behöver åtgärdas',
  processingEyebrow: 'Mötet bearbetas',
  processingNote:
    'Du kan stänga den här vyn. Bearbetningen fortsätter i bakgrunden och originalet finns kvar.',
  stageFailedSealing: 'Inspelningen kunde inte sparas. Ljudet finns kvar.',
  stageFailedTranscribing: 'Inspelningen kunde inte transkriberas. Originalet finns kvar.',
  stageFailedOther: 'Originalinspelningen är säker, men bearbetningen kunde inte slutföras.',
  failedEyebrow: 'Mötet behöver åtgärdas',
  retry: 'Försök igen',
  retryFailed: 'Mötet kunde inte bearbetas på nytt.',
  canvasEyebrow: 'Möte · Originalet sparat',
  noAttendees: 'Inga deltagare tillagda',
  paymentRequired:
    'Företagets användningskredit är slut. En administratör kan lägga till ett kort, försök sedan igen.',
  failureDetails: 'Detaljer',
  retryOwnerOnly: 'Personen som lade till mötet kan försöka bearbeta det igen.',
};

const da: Copy = {
  overview: 'Resumé',
  decisions: 'Beslutninger',
  actionItems: 'Handlingspunkter',
  openQuestions: 'Åbne spørgsmål',
  risks: 'Risici',
  ownerInferred: 'Ansvarlig: {name} (udledt)',
  due: 'Frist {date}',
  dueText: 'Frist: {text}',
  dueInferred: 'Frist {date} (udledt af “{text}”)',
  notesGenerating: 'Forbereder noter…',
  notesStale: 'Udskriften eller talerne er ændret. Noterne opdateres…',
  notesCurrent: 'Noterne er opdaterede.',
  notesFailed: 'Noterne kunne ikke udarbejdes.',
  notesLegacyFailed: 'Udskriften er gemt, men de automatiske noter skal prøves igen.',
  notesNone: 'Ingen noter endnu.',
  retryNotes: 'Prøv noterne igen',
  regenerateNotes: 'Lav noterne igen',
  notesRetryFailed: 'Noterne kunne ikke startes igen.',
  transcriptRows: 'Udskrift {rows}',
  showEvidence: 'Vis belæg i udskriften',
  coveragePartial:
    'Noterne dækker {included} af {total} udskriftssegmenter. Læs resten af udskriften direkte.',
  suggestionsEyebrow: 'Gennemgå før du gemmer',
  suggestionsTitle: 'Mulige opdateringer af virksomhedsviden',
  suggestionsBody: 'Dette er forslag fra mødet. Intet føjes automatisk til virksomhedens viden.',
  reviewInChat: 'Gennemgå i chatten',
  reviewInChatPrompt:
    'Sammenhold dette punkt fra mødet med vores vidensider, og hvis det er varigt og korrekt, så foreslå en opdatering af den rette side til gennemgang: “{text}”',
  relatedContext: 'Relateret virksomhedskontekst',
  stageCapturing: 'Optager',
  stageSealing: 'Gemmer optagelsen',
  stageTranscribing: 'Transskriberer optagelsen',
  stageProcessing: 'Forbereder udskriften',
  stageReady: 'Klar',
  rowNotesGenerating: 'Klar · forbereder noter',
  rowNotesStale: 'Klar · opdaterer noter',
  rowNotesFailed: 'Klar · noterne kræver opmærksomhed',
  rowFailed: 'Kræver opmærksomhed',
  processingEyebrow: 'Mødet behandles',
  processingNote:
    'Du kan lukke denne visning. Behandlingen fortsætter i baggrunden, og originalen bevares.',
  stageFailedSealing: 'Optagelsen kunne ikke gemmes. Lyden er bevaret.',
  stageFailedTranscribing: 'Optagelsen kunne ikke transskriberes. Originalen er bevaret.',
  stageFailedOther: 'Den originale optagelse er sikker, men behandlingen kunne ikke gøres færdig.',
  failedEyebrow: 'Mødet kræver opmærksomhed',
  retry: 'Prøv igen',
  retryFailed: 'Mødet kunne ikke behandles igen.',
  canvasEyebrow: 'Møde · Original bevaret',
  noAttendees: 'Ingen deltagere tilføjet',
  paymentRequired:
    'Jeres virksomheds forbrugskredit er brugt op. En administrator kan tilføje et kort; prøv derefter igen.',
  failureDetails: 'Detaljer',
  retryOwnerOnly: 'Den person, der tilføjede mødet, kan prøve behandlingen igen.',
};

const de: Copy = {
  overview: 'Zusammenfassung',
  decisions: 'Entscheidungen',
  actionItems: 'Aufgaben',
  openQuestions: 'Offene Fragen',
  risks: 'Risiken',
  ownerInferred: 'Verantwortlich: {name} (abgeleitet)',
  due: 'Fällig {date}',
  dueText: 'Fällig: {text}',
  dueInferred: 'Fällig {date} (abgeleitet aus „{text}“)',
  notesGenerating: 'Notizen werden vorbereitet…',
  notesStale: 'Transkript oder Sprecher haben sich geändert. Die Notizen werden aktualisiert…',
  notesCurrent: 'Die Notizen sind aktuell.',
  notesFailed: 'Die Notizen konnten nicht erstellt werden.',
  notesLegacyFailed:
    'Das Transkript ist gesichert, aber die automatischen Notizen brauchen einen neuen Versuch.',
  notesNone: 'Noch keine Notizen.',
  retryNotes: 'Notizen erneut versuchen',
  regenerateNotes: 'Notizen neu erstellen',
  notesRetryFailed: 'Die Notizen konnten nicht neu gestartet werden.',
  transcriptRows: 'Transkript {rows}',
  showEvidence: 'Beleg im Transkript zeigen',
  coveragePartial:
    'Die Notizen decken {included} von {total} Transkriptsegmenten ab. Lesen Sie den Rest des Transkripts direkt.',
  suggestionsEyebrow: 'Vor dem Speichern prüfen',
  suggestionsTitle: 'Mögliche Aktualisierungen des Unternehmenswissens',
  suggestionsBody:
    'Dies sind Vorschläge aus dem Meeting. Nichts wird automatisch zum Unternehmenswissen hinzugefügt.',
  reviewInChat: 'Im Chat prüfen',
  reviewInChatPrompt:
    'Gleiche diesen Punkt aus dem Meeting mit unseren Wissensseiten ab und schlage, falls er dauerhaft und korrekt ist, eine Änderung an der passenden Seite zur Prüfung vor: „{text}“',
  relatedContext: 'Zugehöriger Unternehmenskontext',
  stageCapturing: 'Aufnahme läuft',
  stageSealing: 'Aufnahme wird gespeichert',
  stageTranscribing: 'Aufnahme wird transkribiert',
  stageProcessing: 'Transkript wird vorbereitet',
  stageReady: 'Bereit',
  rowNotesGenerating: 'Bereit · Notizen werden vorbereitet',
  rowNotesStale: 'Bereit · Notizen werden aktualisiert',
  rowNotesFailed: 'Bereit · Notizen brauchen Aufmerksamkeit',
  rowFailed: 'Braucht Aufmerksamkeit',
  processingEyebrow: 'Meeting wird verarbeitet',
  processingNote:
    'Sie können diese Ansicht schließen. Die Verarbeitung läuft im Hintergrund weiter und das Original bleibt erhalten.',
  stageFailedSealing: 'Die Aufnahme konnte nicht gespeichert werden. Der Ton bleibt erhalten.',
  stageFailedTranscribing:
    'Die Aufnahme konnte nicht transkribiert werden. Das Original bleibt erhalten.',
  stageFailedOther:
    'Die Originalaufnahme ist sicher, aber die Verarbeitung konnte nicht abgeschlossen werden.',
  failedEyebrow: 'Meeting braucht Aufmerksamkeit',
  retry: 'Erneut versuchen',
  retryFailed: 'Das Meeting konnte nicht erneut verarbeitet werden.',
  canvasEyebrow: 'Meeting · Original gespeichert',
  noAttendees: 'Keine Teilnehmenden hinzugefügt',
  paymentRequired:
    'Das Nutzungsguthaben Ihres Unternehmens ist aufgebraucht. Ein Administrator kann eine Karte hinzufügen; versuchen Sie es dann erneut.',
  failureDetails: 'Details',
  retryOwnerOnly:
    'Die Person, die dieses Meeting hinzugefügt hat, kann die Verarbeitung erneut versuchen.',
};

const nl: Copy = {
  overview: 'Samenvatting',
  decisions: 'Besluiten',
  actionItems: 'Actiepunten',
  openQuestions: 'Open vragen',
  risks: 'Risico’s',
  ownerInferred: 'Eigenaar: {name} (afgeleid)',
  due: 'Uiterlijk {date}',
  dueText: 'Uiterlijk: {text}',
  dueInferred: 'Uiterlijk {date} (afgeleid uit “{text}”)',
  notesGenerating: 'Notities worden voorbereid…',
  notesStale: 'Het transcript of de sprekers zijn gewijzigd. De notities worden bijgewerkt…',
  notesCurrent: 'De notities zijn bijgewerkt.',
  notesFailed: 'De notities konden niet worden gemaakt.',
  notesLegacyFailed:
    'Het transcript is veilig, maar de automatische notities hebben een nieuwe poging nodig.',
  notesNone: 'Nog geen notities.',
  retryNotes: 'Notities opnieuw proberen',
  regenerateNotes: 'Notities opnieuw maken',
  notesRetryFailed: 'De notities konden niet opnieuw worden gestart.',
  transcriptRows: 'Transcript {rows}',
  showEvidence: 'Bewijs in transcript tonen',
  coveragePartial:
    'De notities beslaan {included} van {total} transcriptsegmenten. Lees de rest van het transcript direct.',
  suggestionsEyebrow: 'Controleren vóór opslaan',
  suggestionsTitle: 'Mogelijke updates van bedrijfskennis',
  suggestionsBody:
    'Dit zijn suggesties uit de vergadering. Er wordt niets automatisch aan de bedrijfskennis toegevoegd.',
  reviewInChat: 'Controleren in chat',
  reviewInChatPrompt:
    'Vergelijk dit punt uit de vergadering met onze kennispagina’s en stel, als het duurzaam en juist is, een wijziging van de juiste pagina voor ter beoordeling: “{text}”',
  relatedContext: 'Gerelateerde bedrijfscontext',
  stageCapturing: 'Opnemen',
  stageSealing: 'Opname opslaan',
  stageTranscribing: 'Opname transcriberen',
  stageProcessing: 'Transcript voorbereiden',
  stageReady: 'Klaar',
  rowNotesGenerating: 'Klaar · notities worden voorbereid',
  rowNotesStale: 'Klaar · notities worden bijgewerkt',
  rowNotesFailed: 'Klaar · notities vragen aandacht',
  rowFailed: 'Vraagt aandacht',
  processingEyebrow: 'Vergadering wordt verwerkt',
  processingNote:
    'U kunt dit venster sluiten. De verwerking gaat op de achtergrond door en het origineel blijft beschikbaar.',
  stageFailedSealing: 'De opname kon niet worden opgeslagen. Het geluid blijft bewaard.',
  stageFailedTranscribing:
    'De opname kon niet worden getranscribeerd. Het origineel blijft bewaard.',
  stageFailedOther: 'De originele opname is veilig, maar de verwerking kon niet worden afgerond.',
  failedEyebrow: 'Vergadering vraagt aandacht',
  retry: 'Opnieuw proberen',
  retryFailed: 'De vergadering kon niet opnieuw worden verwerkt.',
  canvasEyebrow: 'Vergadering · Origineel bewaard',
  noAttendees: 'Geen deelnemers toegevoegd',
  paymentRequired:
    'Het gebruikstegoed van je bedrijf is op. Een beheerder kan een kaart toevoegen; probeer het daarna opnieuw.',
  failureDetails: 'Details',
  retryOwnerOnly:
    'De persoon die deze vergadering heeft toegevoegd, kan de verwerking opnieuw proberen.',
};

const fr: Copy = {
  overview: 'Résumé',
  decisions: 'Décisions',
  actionItems: 'Actions',
  openQuestions: 'Questions ouvertes',
  risks: 'Risques',
  ownerInferred: 'Responsable : {name} (déduit)',
  due: 'Échéance {date}',
  dueText: 'Échéance : {text}',
  dueInferred: 'Échéance {date} (déduite de « {text} »)',
  notesGenerating: 'Préparation des notes…',
  notesStale: 'La transcription ou les intervenants ont changé. Mise à jour des notes…',
  notesCurrent: 'Les notes sont à jour.',
  notesFailed: 'Les notes n’ont pas pu être préparées.',
  notesLegacyFailed:
    'La transcription est en sécurité, mais les notes automatiques doivent être relancées.',
  notesNone: 'Pas encore de notes.',
  retryNotes: 'Relancer les notes',
  regenerateNotes: 'Régénérer les notes',
  notesRetryFailed: 'Les notes n’ont pas pu être relancées.',
  transcriptRows: 'Transcription {rows}',
  showEvidence: 'Afficher la preuve dans la transcription',
  coveragePartial:
    'Les notes couvrent {included} des {total} segments de la transcription. Consultez directement le reste de la transcription.',
  suggestionsEyebrow: 'À vérifier avant d’enregistrer',
  suggestionsTitle: 'Mises à jour possibles des connaissances de l’entreprise',
  suggestionsBody:
    'Ce sont des suggestions issues de la réunion. Rien n’est ajouté automatiquement aux connaissances de l’entreprise.',
  reviewInChat: 'Examiner dans le chat',
  reviewInChatPrompt:
    'Compare ce point de la réunion avec nos pages de connaissances et, s’il est durable et exact, suggère une modification de la bonne page à faire valider : « {text} »',
  relatedContext: 'Contexte d’entreprise associé',
  stageCapturing: 'Enregistrement en cours',
  stageSealing: 'Enregistrement du fichier',
  stageTranscribing: 'Transcription de l’enregistrement',
  stageProcessing: 'Préparation de la transcription',
  stageReady: 'Prête',
  rowNotesGenerating: 'Prête · préparation des notes',
  rowNotesStale: 'Prête · mise à jour des notes',
  rowNotesFailed: 'Prête · les notes demandent votre attention',
  rowFailed: 'Demande votre attention',
  processingEyebrow: 'Traitement de la réunion',
  processingNote:
    'Vous pouvez fermer cette vue. Le traitement continue en arrière-plan et l’original reste disponible.',
  stageFailedSealing: 'L’enregistrement n’a pas pu être sauvegardé. L’audio est conservé.',
  stageFailedTranscribing: 'L’enregistrement n’a pas pu être transcrit. L’original est conservé.',
  stageFailedOther:
    'L’enregistrement original est en sécurité, mais le traitement n’a pas pu aboutir.',
  failedEyebrow: 'La réunion demande votre attention',
  retry: 'Réessayer',
  retryFailed: 'La réunion n’a pas pu être retraitée.',
  canvasEyebrow: 'Réunion · Original conservé',
  noAttendees: 'Aucun participant ajouté',
  paymentRequired:
    'Le crédit d’utilisation de votre entreprise est épuisé. Un administrateur peut ajouter une carte ; réessayez ensuite.',
  failureDetails: 'Détails',
  retryOwnerOnly: 'La personne qui a ajouté cette réunion peut relancer le traitement.',
};

const es: Copy = {
  overview: 'Resumen',
  decisions: 'Decisiones',
  actionItems: 'Acciones',
  openQuestions: 'Preguntas abiertas',
  risks: 'Riesgos',
  ownerInferred: 'Responsable: {name} (deducido)',
  due: 'Vence el {date}',
  dueText: 'Plazo: {text}',
  dueInferred: 'Vence el {date} (deducido de «{text}»)',
  notesGenerating: 'Preparando las notas…',
  notesStale: 'La transcripción o los interlocutores cambiaron. Actualizando las notas…',
  notesCurrent: 'Las notas están al día.',
  notesFailed: 'No se pudieron preparar las notas.',
  notesLegacyFailed:
    'La transcripción está a salvo, pero las notas automáticas necesitan otro intento.',
  notesNone: 'Todavía no hay notas.',
  retryNotes: 'Reintentar las notas',
  regenerateNotes: 'Regenerar las notas',
  notesRetryFailed: 'No se pudieron reintentar las notas.',
  transcriptRows: 'Transcripción {rows}',
  showEvidence: 'Mostrar la evidencia en la transcripción',
  coveragePartial:
    'Las notas cubren {included} de {total} segmentos de la transcripción. Revisa directamente el resto de la transcripción.',
  suggestionsEyebrow: 'Revisar antes de guardar',
  suggestionsTitle: 'Posibles actualizaciones del conocimiento de la empresa',
  suggestionsBody:
    'Son sugerencias de la reunión. No se añade nada al conocimiento de la empresa automáticamente.',
  reviewInChat: 'Revisar en el chat',
  reviewInChatPrompt:
    'Compara este punto de la reunión con nuestras páginas de conocimiento y, si es duradero y exacto, sugiere un cambio en la página adecuada para que se revise: «{text}»',
  relatedContext: 'Contexto de empresa relacionado',
  stageCapturing: 'Grabando',
  stageSealing: 'Guardando la grabación',
  stageTranscribing: 'Transcribiendo la grabación',
  stageProcessing: 'Preparando la transcripción',
  stageReady: 'Lista',
  rowNotesGenerating: 'Lista · preparando notas',
  rowNotesStale: 'Lista · actualizando notas',
  rowNotesFailed: 'Lista · las notas requieren atención',
  rowFailed: 'Requiere atención',
  processingEyebrow: 'Procesando la reunión',
  processingNote:
    'Puedes cerrar esta vista. El procesamiento continúa en segundo plano y el original sigue disponible.',
  stageFailedSealing: 'No se pudo guardar la grabación. El audio se conserva.',
  stageFailedTranscribing: 'No se pudo transcribir la grabación. El original se conserva.',
  stageFailedOther: 'La grabación original está a salvo, pero el procesamiento no pudo terminar.',
  failedEyebrow: 'La reunión requiere atención',
  retry: 'Reintentar',
  retryFailed: 'No se pudo volver a procesar la reunión.',
  canvasEyebrow: 'Reunión · Original conservado',
  noAttendees: 'No se han añadido asistentes',
  paymentRequired:
    'El crédito de uso de tu empresa se ha agotado. Un administrador puede añadir una tarjeta; después, vuelve a intentarlo.',
  failureDetails: 'Detalles',
  retryOwnerOnly: 'La persona que añadió esta reunión puede volver a intentar el procesamiento.',
};

const it: Copy = {
  overview: 'Riepilogo',
  decisions: 'Decisioni',
  actionItems: 'Azioni',
  openQuestions: 'Domande aperte',
  risks: 'Rischi',
  ownerInferred: 'Responsabile: {name} (dedotto)',
  due: 'Scadenza {date}',
  dueText: 'Scadenza: {text}',
  dueInferred: 'Scadenza {date} (dedotta da «{text}»)',
  notesGenerating: 'Preparazione delle note…',
  notesStale: 'La trascrizione o i relatori sono cambiati. Aggiornamento delle note…',
  notesCurrent: 'Le note sono aggiornate.',
  notesFailed: 'Non è stato possibile preparare le note.',
  notesLegacyFailed:
    'La trascrizione è al sicuro, ma le note automatiche richiedono un nuovo tentativo.',
  notesNone: 'Ancora nessuna nota.',
  retryNotes: 'Riprova le note',
  regenerateNotes: 'Rigenera le note',
  notesRetryFailed: 'Non è stato possibile riavviare le note.',
  transcriptRows: 'Trascrizione {rows}',
  showEvidence: 'Mostra la prova nella trascrizione',
  coveragePartial:
    'Le note coprono {included} di {total} segmenti della trascrizione. Consulta direttamente il resto della trascrizione.',
  suggestionsEyebrow: 'Verifica prima di salvare',
  suggestionsTitle: 'Possibili aggiornamenti della conoscenza aziendale',
  suggestionsBody:
    'Sono suggerimenti emersi dalla riunione. Nulla viene aggiunto automaticamente alla conoscenza aziendale.',
  reviewInChat: 'Rivedi in chat',
  reviewInChatPrompt:
    'Confronta questo punto della riunione con le nostre pagine di conoscenza e, se è duraturo e accurato, suggerisci una modifica alla pagina giusta da far revisionare: «{text}»',
  relatedContext: 'Contesto aziendale correlato',
  stageCapturing: 'Registrazione in corso',
  stageSealing: 'Salvataggio della registrazione',
  stageTranscribing: 'Trascrizione della registrazione',
  stageProcessing: 'Preparazione della trascrizione',
  stageReady: 'Pronta',
  rowNotesGenerating: 'Pronta · preparazione delle note',
  rowNotesStale: 'Pronta · aggiornamento delle note',
  rowNotesFailed: 'Pronta · le note richiedono attenzione',
  rowFailed: 'Richiede attenzione',
  processingEyebrow: 'Elaborazione della riunione',
  processingNote:
    'Puoi chiudere questa vista. L’elaborazione continua in background e l’originale resta disponibile.',
  stageFailedSealing: 'Non è stato possibile salvare la registrazione. L’audio è conservato.',
  stageFailedTranscribing:
    'Non è stato possibile trascrivere la registrazione. L’originale è conservato.',
  stageFailedOther:
    'La registrazione originale è al sicuro, ma non è stato possibile completare l’elaborazione.',
  failedEyebrow: 'La riunione richiede attenzione',
  retry: 'Riprova',
  retryFailed: 'Non è stato possibile rielaborare la riunione.',
  canvasEyebrow: 'Riunione · Originale conservato',
  noAttendees: 'Nessun partecipante aggiunto',
  paymentRequired:
    'Il credito di utilizzo della tua azienda è esaurito. Un amministratore può aggiungere una carta, poi riprova.',
  failureDetails: 'Dettagli',
  retryOwnerOnly: 'La persona che ha aggiunto questa riunione può riprovare l’elaborazione.',
};

const pt: Copy = {
  overview: 'Resumo',
  decisions: 'Decisões',
  actionItems: 'Ações',
  openQuestions: 'Questões em aberto',
  risks: 'Riscos',
  ownerInferred: 'Responsável: {name} (inferido)',
  due: 'Prazo {date}',
  dueText: 'Prazo: {text}',
  dueInferred: 'Prazo {date} (inferido de “{text}”)',
  notesGenerating: 'A preparar as notas…',
  notesStale: 'A transcrição ou os oradores mudaram. A atualizar as notas…',
  notesCurrent: 'As notas estão atualizadas.',
  notesFailed: 'Não foi possível preparar as notas.',
  notesLegacyFailed:
    'A transcrição está segura, mas as notas automáticas precisam de nova tentativa.',
  notesNone: 'Ainda não há notas.',
  retryNotes: 'Tentar novamente as notas',
  regenerateNotes: 'Gerar as notas de novo',
  notesRetryFailed: 'Não foi possível reiniciar as notas.',
  transcriptRows: 'Transcrição {rows}',
  showEvidence: 'Mostrar a evidência na transcrição',
  coveragePartial:
    'As notas cobrem {included} de {total} segmentos da transcrição. Consulte diretamente o resto da transcrição.',
  suggestionsEyebrow: 'Rever antes de guardar',
  suggestionsTitle: 'Possíveis atualizações do conhecimento da empresa',
  suggestionsBody:
    'Estas são sugestões da reunião. Nada é adicionado automaticamente ao conhecimento da empresa.',
  reviewInChat: 'Rever no chat',
  reviewInChatPrompt:
    'Compara este ponto da reunião com as nossas páginas de conhecimento e, se for duradouro e exato, sugere uma alteração à página certa para revisão: «{text}»',
  relatedContext: 'Contexto da empresa relacionado',
  stageCapturing: 'A gravar',
  stageSealing: 'A guardar a gravação',
  stageTranscribing: 'A transcrever a gravação',
  stageProcessing: 'A preparar a transcrição',
  stageReady: 'Pronta',
  rowNotesGenerating: 'Pronta · a preparar notas',
  rowNotesStale: 'Pronta · a atualizar notas',
  rowNotesFailed: 'Pronta · as notas precisam de atenção',
  rowFailed: 'Precisa de atenção',
  processingEyebrow: 'A processar a reunião',
  processingNote:
    'Pode fechar esta vista. O processamento continua em segundo plano e o original continua disponível.',
  stageFailedSealing: 'Não foi possível guardar a gravação. O áudio é mantido.',
  stageFailedTranscribing: 'Não foi possível transcrever a gravação. O original é mantido.',
  stageFailedOther: 'A gravação original está segura, mas o processamento não pôde terminar.',
  failedEyebrow: 'A reunião precisa de atenção',
  retry: 'Tentar novamente',
  retryFailed: 'Não foi possível processar novamente a reunião.',
  canvasEyebrow: 'Reunião · Original guardado',
  noAttendees: 'Nenhum participante adicionado',
  paymentRequired:
    'O crédito de utilização da sua empresa esgotou-se. Um administrador pode adicionar um cartão; depois tente novamente.',
  failureDetails: 'Detalhes',
  retryOwnerOnly: 'A pessoa que adicionou esta reunião pode tentar processá-la novamente.',
};

const fi: Copy = {
  overview: 'Yhteenveto',
  decisions: 'Päätökset',
  actionItems: 'Toimenpiteet',
  openQuestions: 'Avoimet kysymykset',
  risks: 'Riskit',
  ownerInferred: 'Vastuuhenkilö: {name} (päätelty)',
  due: 'Määräpäivä {date}',
  dueText: 'Määräpäivä: {text}',
  dueInferred: 'Määräpäivä {date} (päätelty ilmauksesta ”{text}”)',
  notesGenerating: 'Muistiinpanoja valmistellaan…',
  notesStale: 'Litterointi tai puhujat muuttuivat. Muistiinpanoja päivitetään…',
  notesCurrent: 'Muistiinpanot ovat ajan tasalla.',
  notesFailed: 'Muistiinpanoja ei voitu laatia.',
  notesLegacyFailed:
    'Litterointi on tallessa, mutta automaattiset muistiinpanot on yritettävä uudelleen.',
  notesNone: 'Ei vielä muistiinpanoja.',
  retryNotes: 'Yritä muistiinpanoja uudelleen',
  regenerateNotes: 'Laadi muistiinpanot uudelleen',
  notesRetryFailed: 'Muistiinpanoja ei voitu käynnistää uudelleen.',
  transcriptRows: 'Litterointi {rows}',
  showEvidence: 'Näytä todiste litteroinnissa',
  coveragePartial:
    'Muistiinpanot kattavat {included}/{total} litterointisegmenttiä. Lue loput litteroinnista suoraan.',
  suggestionsEyebrow: 'Tarkista ennen tallennusta',
  suggestionsTitle: 'Mahdolliset päivitykset yrityksen tietoon',
  suggestionsBody:
    'Nämä ovat ehdotuksia kokouksesta. Mitään ei lisätä yrityksen tietoon automaattisesti.',
  reviewInChat: 'Tarkista chatissa',
  reviewInChatPrompt:
    'Vertaa tätä kokouksen kohtaa tietämyssivuihimme ja, jos se on pysyvä ja paikkansapitävä, ehdota muutosta oikealle sivulle tarkistettavaksi: ”{text}”',
  relatedContext: 'Liittyvä yrityskonteksti',
  stageCapturing: 'Tallennetaan',
  stageSealing: 'Tallennetta tallennetaan',
  stageTranscribing: 'Tallennetta litteroidaan',
  stageProcessing: 'Litterointia valmistellaan',
  stageReady: 'Valmis',
  rowNotesGenerating: 'Valmis · muistiinpanoja valmistellaan',
  rowNotesStale: 'Valmis · muistiinpanoja päivitetään',
  rowNotesFailed: 'Valmis · muistiinpanot vaativat huomiota',
  rowFailed: 'Vaatii huomiota',
  processingEyebrow: 'Kokousta käsitellään',
  processingNote: 'Voit sulkea tämän näkymän. Käsittely jatkuu taustalla ja alkuperäinen säilyy.',
  stageFailedSealing: 'Tallennetta ei voitu tallentaa. Ääni säilytetään.',
  stageFailedTranscribing: 'Tallennetta ei voitu litteroida. Alkuperäinen säilytetään.',
  stageFailedOther: 'Alkuperäinen tallenne on tallessa, mutta käsittelyä ei saatu valmiiksi.',
  failedEyebrow: 'Kokous vaatii huomiota',
  retry: 'Yritä uudelleen',
  retryFailed: 'Kokousta ei voitu käsitellä uudelleen.',
  canvasEyebrow: 'Kokous · Alkuperäinen säilytetty',
  noAttendees: 'Osallistujia ei ole lisätty',
  paymentRequired:
    'Yrityksesi käyttösaldo on käytetty. Ylläpitäjä voi lisätä kortin, yritä sitten uudelleen.',
  failureDetails: 'Tiedot',
  retryOwnerOnly: 'Kokouksen lisännyt henkilö voi yrittää käsittelyä uudelleen.',
};

const et: Copy = {
  overview: 'Kokkuvõte',
  decisions: 'Otsused',
  actionItems: 'Tegevused',
  openQuestions: 'Lahtised küsimused',
  risks: 'Riskid',
  ownerInferred: 'Vastutaja: {name} (tuletatud)',
  due: 'Tähtaeg {date}',
  dueText: 'Tähtaeg: {text}',
  dueInferred: 'Tähtaeg {date} (tuletatud väljendist „{text}“)',
  notesGenerating: 'Märkmeid koostatakse…',
  notesStale: 'Transkriptsioon või kõnelejad muutusid. Märkmeid värskendatakse…',
  notesCurrent: 'Märkmed on ajakohased.',
  notesFailed: 'Märkmeid ei õnnestunud koostada.',
  notesLegacyFailed: 'Transkriptsioon on alles, kuid automaatseid märkmeid tuleb uuesti proovida.',
  notesNone: 'Märkmeid veel pole.',
  retryNotes: 'Proovi märkmeid uuesti',
  regenerateNotes: 'Koosta märkmed uuesti',
  notesRetryFailed: 'Märkmeid ei õnnestunud uuesti käivitada.',
  transcriptRows: 'Transkriptsioon {rows}',
  showEvidence: 'Näita tõendit transkriptsioonis',
  coveragePartial:
    'Märkmed hõlmavad {included} transkriptsiooni lõiku {total}-st. Loe ülejäänud transkriptsiooni otse.',
  suggestionsEyebrow: 'Vaata enne salvestamist üle',
  suggestionsTitle: 'Võimalikud ettevõtte teadmiste uuendused',
  suggestionsBody:
    'Need on koosolekust tulenevad ettepanekud. Midagi ei lisata ettevõtte teadmistesse automaatselt.',
  reviewInChat: 'Vaata vestluses üle',
  reviewInChatPrompt:
    'Võrdle seda koosoleku punkti meie teadmiste lehtedega ja kui see on püsiv ja täpne, soovita õigele lehele muudatust ülevaatamiseks: „{text}“',
  relatedContext: 'Seotud ettevõtte kontekst',
  stageCapturing: 'Salvestab',
  stageSealing: 'Salvestist salvestatakse',
  stageTranscribing: 'Salvestist transkribeeritakse',
  stageProcessing: 'Transkriptsiooni valmistatakse ette',
  stageReady: 'Valmis',
  rowNotesGenerating: 'Valmis · märkmeid koostatakse',
  rowNotesStale: 'Valmis · märkmeid värskendatakse',
  rowNotesFailed: 'Valmis · märkmed vajavad tähelepanu',
  rowFailed: 'Vajab tähelepanu',
  processingEyebrow: 'Koosolekut töödeldakse',
  processingNote: 'Võid selle vaate sulgeda. Töötlemine jätkub taustal ja originaal jääb alles.',
  stageFailedSealing: 'Salvestist ei õnnestunud salvestada. Heli jääb alles.',
  stageFailedTranscribing: 'Salvestist ei õnnestunud transkribeerida. Originaal jääb alles.',
  stageFailedOther: 'Algne salvestis on alles, kuid töötlemist ei õnnestunud lõpetada.',
  failedEyebrow: 'Koosolek vajab tähelepanu',
  retry: 'Proovi uuesti',
  retryFailed: 'Koosolekut ei õnnestunud uuesti töödelda.',
  canvasEyebrow: 'Koosolek · Originaal säilitatud',
  noAttendees: 'Osalejaid pole lisatud',
  paymentRequired:
    'Ettevõtte kasutuskrediit on otsas. Administraator saab lisada kaardi, seejärel proovi uuesti.',
  failureDetails: 'Üksikasjad',
  retryOwnerOnly: 'Selle koosoleku lisanud inimene saab töötlemist uuesti proovida.',
};

const lv: Copy = {
  overview: 'Kopsavilkums',
  decisions: 'Lēmumi',
  actionItems: 'Darbības',
  openQuestions: 'Atklātie jautājumi',
  risks: 'Riski',
  ownerInferred: 'Atbildīgais: {name} (secināts)',
  due: 'Termiņš {date}',
  dueText: 'Termiņš: {text}',
  dueInferred: 'Termiņš {date} (secināts no “{text}”)',
  notesGenerating: 'Gatavo piezīmes…',
  notesStale: 'Transkripcija vai runātāji mainījās. Piezīmes tiek atjauninātas…',
  notesCurrent: 'Piezīmes ir aktuālas.',
  notesFailed: 'Piezīmes neizdevās sagatavot.',
  notesLegacyFailed: 'Transkripcija ir saglabāta, bet automātiskās piezīmes jāmēģina vēlreiz.',
  notesNone: 'Piezīmju vēl nav.',
  retryNotes: 'Mēģināt piezīmes vēlreiz',
  regenerateNotes: 'Izveidot piezīmes no jauna',
  notesRetryFailed: 'Piezīmes neizdevās palaist atkārtoti.',
  transcriptRows: 'Transkripcija {rows}',
  showEvidence: 'Rādīt pierādījumu transkripcijā',
  coveragePartial:
    'Piezīmes aptver {included} no {total} transkripcijas segmentiem. Pārējo transkripciju skatiet tieši.',
  suggestionsEyebrow: 'Pārskatiet pirms saglabāšanas',
  suggestionsTitle: 'Iespējamie uzņēmuma zināšanu atjauninājumi',
  suggestionsBody:
    'Tie ir sanāksmes ieteikumi. Uzņēmuma zināšanām nekas netiek pievienots automātiski.',
  reviewInChat: 'Pārskatīt tērzēšanā',
  reviewInChatPrompt:
    'Salīdzini šo sanāksmes punktu ar mūsu zināšanu lapām un, ja tas ir paliekošs un precīzs, iesaki izmaiņas pareizajā lapā pārskatīšanai: “{text}”',
  relatedContext: 'Saistītais uzņēmuma konteksts',
  stageCapturing: 'Notiek ierakstīšana',
  stageSealing: 'Saglabā ierakstu',
  stageTranscribing: 'Transkribē ierakstu',
  stageProcessing: 'Gatavo transkripciju',
  stageReady: 'Gatavs',
  rowNotesGenerating: 'Gatavs · gatavo piezīmes',
  rowNotesStale: 'Gatavs · atjaunina piezīmes',
  rowNotesFailed: 'Gatavs · piezīmēm vajadzīga uzmanība',
  rowFailed: 'Vajadzīga uzmanība',
  processingEyebrow: 'Sanāksme tiek apstrādāta',
  processingNote: 'Varat aizvērt šo skatu. Apstrāde turpinās fonā, un oriģināls paliek pieejams.',
  stageFailedSealing: 'Ierakstu neizdevās saglabāt. Audio ir saglabāts.',
  stageFailedTranscribing: 'Ierakstu neizdevās transkribēt. Oriģināls ir saglabāts.',
  stageFailedOther: 'Oriģinālais ieraksts ir drošībā, bet apstrādi neizdevās pabeigt.',
  failedEyebrow: 'Sanāksmei vajadzīga uzmanība',
  retry: 'Mēģināt vēlreiz',
  retryFailed: 'Sanāksmi neizdevās apstrādāt atkārtoti.',
  canvasEyebrow: 'Sanāksme · Oriģināls saglabāts',
  noAttendees: 'Dalībnieki nav pievienoti',
  paymentRequired:
    'Jūsu uzņēmuma lietošanas kredīts ir izlietots. Administrators var pievienot karti, pēc tam mēģiniet vēlreiz.',
  failureDetails: 'Sīkāka informācija',
  retryOwnerOnly: 'Persona, kas pievienoja šo sanāksmi, var mēģināt apstrādi vēlreiz.',
};

const lt: Copy = {
  overview: 'Santrauka',
  decisions: 'Sprendimai',
  actionItems: 'Veiksmai',
  openQuestions: 'Atviri klausimai',
  risks: 'Rizikos',
  ownerInferred: 'Atsakingas: {name} (numanoma)',
  due: 'Terminas {date}',
  dueText: 'Terminas: {text}',
  dueInferred: 'Terminas {date} (numanoma iš „{text}“)',
  notesGenerating: 'Rengiamos pastabos…',
  notesStale: 'Pasikeitė nuorašas arba kalbėtojai. Pastabos atnaujinamos…',
  notesCurrent: 'Pastabos atnaujintos.',
  notesFailed: 'Pastabų parengti nepavyko.',
  notesLegacyFailed: 'Nuorašas išsaugotas, bet automatines pastabas reikia bandyti dar kartą.',
  notesNone: 'Pastabų dar nėra.',
  retryNotes: 'Bandyti pastabas dar kartą',
  regenerateNotes: 'Parengti pastabas iš naujo',
  notesRetryFailed: 'Pastabų paleisti iš naujo nepavyko.',
  transcriptRows: 'Nuorašas {rows}',
  showEvidence: 'Rodyti įrodymą nuoraše',
  coveragePartial:
    'Pastabos apima {included} iš {total} nuorašo segmentų. Likusį nuorašą peržiūrėkite tiesiogiai.',
  suggestionsEyebrow: 'Peržiūrėkite prieš išsaugodami',
  suggestionsTitle: 'Galimi įmonės žinių atnaujinimai',
  suggestionsBody: 'Tai susitikimo pasiūlymai. Į įmonės žinias niekas nepridedama automatiškai.',
  reviewInChat: 'Peržiūrėti pokalbyje',
  reviewInChatPrompt:
    'Palygink šį susitikimo punktą su mūsų žinių puslapiais ir, jei jis ilgalaikis ir tikslus, pasiūlyk tinkamo puslapio pakeitimą peržiūrai: „{text}“',
  relatedContext: 'Susijęs įmonės kontekstas',
  stageCapturing: 'Įrašoma',
  stageSealing: 'Įrašas išsaugomas',
  stageTranscribing: 'Įrašas transkribuojamas',
  stageProcessing: 'Rengiamas nuorašas',
  stageReady: 'Paruošta',
  rowNotesGenerating: 'Paruošta · rengiamos pastabos',
  rowNotesStale: 'Paruošta · atnaujinamos pastabos',
  rowNotesFailed: 'Paruošta · pastaboms reikia dėmesio',
  rowFailed: 'Reikia dėmesio',
  processingEyebrow: 'Susitikimas apdorojamas',
  processingNote:
    'Galite uždaryti šį rodinį. Apdorojimas tęsiamas fone, o originalas lieka prieinamas.',
  stageFailedSealing: 'Įrašo išsaugoti nepavyko. Garsas išsaugotas.',
  stageFailedTranscribing: 'Įrašo transkribuoti nepavyko. Originalas išsaugotas.',
  stageFailedOther: 'Originalus įrašas saugus, bet apdorojimo užbaigti nepavyko.',
  failedEyebrow: 'Susitikimui reikia dėmesio',
  retry: 'Bandyti dar kartą',
  retryFailed: 'Susitikimo apdoroti iš naujo nepavyko.',
  canvasEyebrow: 'Susitikimas · Originalas išsaugotas',
  noAttendees: 'Dalyvių nepridėta',
  paymentRequired:
    'Jūsų įmonės naudojimo kreditas išnaudotas. Administratorius gali pridėti kortelę, tada bandykite dar kartą.',
  failureDetails: 'Išsami informacija',
  retryOwnerOnly: 'Šį susitikimą pridėjęs asmuo gali bandyti apdoroti dar kartą.',
};

const pl: Copy = {
  overview: 'Podsumowanie',
  decisions: 'Decyzje',
  actionItems: 'Zadania',
  openQuestions: 'Otwarte pytania',
  risks: 'Ryzyka',
  ownerInferred: 'Odpowiedzialny: {name} (wywnioskowane)',
  due: 'Termin {date}',
  dueText: 'Termin: {text}',
  dueInferred: 'Termin {date} (wywnioskowano z „{text}”)',
  notesGenerating: 'Przygotowywanie notatek…',
  notesStale: 'Transkrypcja lub mówcy się zmienili. Notatki są odświeżane…',
  notesCurrent: 'Notatki są aktualne.',
  notesFailed: 'Nie udało się przygotować notatek.',
  notesLegacyFailed:
    'Transkrypcja jest bezpieczna, ale automatyczne notatki wymagają ponownej próby.',
  notesNone: 'Nie ma jeszcze notatek.',
  retryNotes: 'Ponów notatki',
  regenerateNotes: 'Wygeneruj notatki ponownie',
  notesRetryFailed: 'Nie udało się ponowić notatek.',
  transcriptRows: 'Transkrypcja {rows}',
  showEvidence: 'Pokaż dowód w transkrypcji',
  coveragePartial:
    'Notatki obejmują {included} z {total} segmentów transkrypcji. Resztę transkrypcji przejrzyj bezpośrednio.',
  suggestionsEyebrow: 'Sprawdź przed zapisaniem',
  suggestionsTitle: 'Możliwe aktualizacje wiedzy firmy',
  suggestionsBody: 'To sugestie ze spotkania. Nic nie jest automatycznie dodawane do wiedzy firmy.',
  reviewInChat: 'Przejrzyj w czacie',
  reviewInChatPrompt:
    'Porównaj ten punkt ze spotkania z naszymi stronami wiedzy i, jeśli jest trwały i trafny, zaproponuj zmianę na właściwej stronie do przejrzenia: „{text}”',
  relatedContext: 'Powiązany kontekst firmy',
  stageCapturing: 'Nagrywanie',
  stageSealing: 'Zapisywanie nagrania',
  stageTranscribing: 'Transkrypcja nagrania',
  stageProcessing: 'Przygotowywanie transkrypcji',
  stageReady: 'Gotowe',
  rowNotesGenerating: 'Gotowe · przygotowywanie notatek',
  rowNotesStale: 'Gotowe · odświeżanie notatek',
  rowNotesFailed: 'Gotowe · notatki wymagają uwagi',
  rowFailed: 'Wymaga uwagi',
  processingEyebrow: 'Przetwarzanie spotkania',
  processingNote:
    'Możesz zamknąć ten widok. Przetwarzanie trwa w tle, a oryginał pozostaje dostępny.',
  stageFailedSealing: 'Nie udało się zapisać nagrania. Dźwięk został zachowany.',
  stageFailedTranscribing: 'Nie udało się przetranskrybować nagrania. Oryginał został zachowany.',
  stageFailedOther:
    'Oryginalne nagranie jest bezpieczne, ale przetwarzania nie udało się ukończyć.',
  failedEyebrow: 'Spotkanie wymaga uwagi',
  retry: 'Spróbuj ponownie',
  retryFailed: 'Nie udało się ponownie przetworzyć spotkania.',
  canvasEyebrow: 'Spotkanie · Oryginał zachowany',
  noAttendees: 'Nie dodano uczestników',
  paymentRequired:
    'Kredyt na korzystanie Twojej firmy się wyczerpał. Administrator może dodać kartę, a potem spróbuj ponownie.',
  failureDetails: 'Szczegóły',
  retryOwnerOnly: 'Osoba, która dodała to spotkanie, może ponowić przetwarzanie.',
};

const cs: Copy = {
  overview: 'Shrnutí',
  decisions: 'Rozhodnutí',
  actionItems: 'Úkoly',
  openQuestions: 'Otevřené otázky',
  risks: 'Rizika',
  ownerInferred: 'Odpovídá: {name} (odvozeno)',
  due: 'Termín {date}',
  dueText: 'Termín: {text}',
  dueInferred: 'Termín {date} (odvozeno z „{text}“)',
  notesGenerating: 'Připravují se poznámky…',
  notesStale: 'Přepis nebo mluvčí se změnili. Poznámky se obnovují…',
  notesCurrent: 'Poznámky jsou aktuální.',
  notesFailed: 'Poznámky se nepodařilo připravit.',
  notesLegacyFailed: 'Přepis je v bezpečí, ale automatické poznámky je třeba zkusit znovu.',
  notesNone: 'Zatím žádné poznámky.',
  retryNotes: 'Zkusit poznámky znovu',
  regenerateNotes: 'Vytvořit poznámky znovu',
  notesRetryFailed: 'Poznámky se nepodařilo spustit znovu.',
  transcriptRows: 'Přepis {rows}',
  showEvidence: 'Zobrazit doklad v přepisu',
  coveragePartial:
    'Poznámky pokrývají {included} z {total} segmentů přepisu. Zbytek přepisu si projděte přímo.',
  suggestionsEyebrow: 'Před uložením zkontrolujte',
  suggestionsTitle: 'Možné aktualizace firemních znalostí',
  suggestionsBody: 'Jde o návrhy ze schůzky. Nic se do firemních znalostí nepřidává automaticky.',
  reviewInChat: 'Zkontrolovat v chatu',
  reviewInChatPrompt:
    'Porovnej tento bod ze schůzky s našimi stránkami znalostí a pokud je trvalý a přesný, navrhni změnu správné stránky ke kontrole: „{text}“',
  relatedContext: 'Související firemní kontext',
  stageCapturing: 'Nahrává se',
  stageSealing: 'Ukládá se nahrávka',
  stageTranscribing: 'Přepisuje se nahrávka',
  stageProcessing: 'Připravuje se přepis',
  stageReady: 'Hotovo',
  rowNotesGenerating: 'Hotovo · připravují se poznámky',
  rowNotesStale: 'Hotovo · obnovují se poznámky',
  rowNotesFailed: 'Hotovo · poznámky vyžadují pozornost',
  rowFailed: 'Vyžaduje pozornost',
  processingEyebrow: 'Schůzka se zpracovává',
  processingNote:
    'Toto zobrazení můžete zavřít. Zpracování pokračuje na pozadí a originál zůstává k dispozici.',
  stageFailedSealing: 'Nahrávku se nepodařilo uložit. Zvuk zůstává zachován.',
  stageFailedTranscribing: 'Nahrávku se nepodařilo přepsat. Originál zůstává zachován.',
  stageFailedOther: 'Původní nahrávka je v bezpečí, ale zpracování se nepodařilo dokončit.',
  failedEyebrow: 'Schůzka vyžaduje pozornost',
  retry: 'Zkusit znovu',
  retryFailed: 'Schůzku se nepodařilo zpracovat znovu.',
  canvasEyebrow: 'Schůzka · Originál zachován',
  noAttendees: 'Nebyli přidáni žádní účastníci',
  paymentRequired:
    'Kredit na využití vaší firmy je vyčerpaný. Správce může přidat kartu, pak to zkuste znovu.',
  failureDetails: 'Podrobnosti',
  retryOwnerOnly: 'Osoba, která tuto schůzku přidala, může zpracování zkusit znovu.',
};

const sk: Copy = {
  overview: 'Zhrnutie',
  decisions: 'Rozhodnutia',
  actionItems: 'Úlohy',
  openQuestions: 'Otvorené otázky',
  risks: 'Riziká',
  ownerInferred: 'Zodpovedá: {name} (odvodené)',
  due: 'Termín {date}',
  dueText: 'Termín: {text}',
  dueInferred: 'Termín {date} (odvodené z „{text}“)',
  notesGenerating: 'Pripravujú sa poznámky…',
  notesStale: 'Prepis alebo rečníci sa zmenili. Poznámky sa obnovujú…',
  notesCurrent: 'Poznámky sú aktuálne.',
  notesFailed: 'Poznámky sa nepodarilo pripraviť.',
  notesLegacyFailed: 'Prepis je v bezpečí, ale automatické poznámky treba skúsiť znova.',
  notesNone: 'Zatiaľ žiadne poznámky.',
  retryNotes: 'Skúsiť poznámky znova',
  regenerateNotes: 'Vytvoriť poznámky znova',
  notesRetryFailed: 'Poznámky sa nepodarilo spustiť znova.',
  transcriptRows: 'Prepis {rows}',
  showEvidence: 'Zobraziť doklad v prepise',
  coveragePartial:
    'Poznámky pokrývajú {included} z {total} segmentov prepisu. Zvyšok prepisu si prejdite priamo.',
  suggestionsEyebrow: 'Pred uložením skontrolujte',
  suggestionsTitle: 'Možné aktualizácie firemných znalostí',
  suggestionsBody:
    'Ide o návrhy zo stretnutia. Nič sa do firemných znalostí nepridáva automaticky.',
  reviewInChat: 'Skontrolovať v chate',
  reviewInChatPrompt:
    'Porovnaj tento bod zo stretnutia s našimi stránkami znalostí a ak je trvalý a presný, navrhni zmenu správnej stránky na kontrolu: „{text}“',
  relatedContext: 'Súvisiaci firemný kontext',
  stageCapturing: 'Nahráva sa',
  stageSealing: 'Ukladá sa nahrávka',
  stageTranscribing: 'Prepisuje sa nahrávka',
  stageProcessing: 'Pripravuje sa prepis',
  stageReady: 'Hotovo',
  rowNotesGenerating: 'Hotovo · pripravujú sa poznámky',
  rowNotesStale: 'Hotovo · obnovujú sa poznámky',
  rowNotesFailed: 'Hotovo · poznámky si vyžadujú pozornosť',
  rowFailed: 'Vyžaduje pozornosť',
  processingEyebrow: 'Stretnutie sa spracúva',
  processingNote:
    'Toto zobrazenie môžete zavrieť. Spracovanie pokračuje na pozadí a originál zostáva dostupný.',
  stageFailedSealing: 'Nahrávku sa nepodarilo uložiť. Zvuk zostáva zachovaný.',
  stageFailedTranscribing: 'Nahrávku sa nepodarilo prepísať. Originál zostáva zachovaný.',
  stageFailedOther: 'Pôvodná nahrávka je v bezpečí, ale spracovanie sa nepodarilo dokončiť.',
  failedEyebrow: 'Stretnutie si vyžaduje pozornosť',
  retry: 'Skúsiť znova',
  retryFailed: 'Stretnutie sa nepodarilo spracovať znova.',
  canvasEyebrow: 'Stretnutie · Originál zachovaný',
  noAttendees: 'Neboli pridaní žiadni účastníci',
  paymentRequired:
    'Kredit na využívanie vašej firmy je vyčerpaný. Správca môže pridať kartu, potom to skúste znova.',
  failureDetails: 'Podrobnosti',
  retryOwnerOnly: 'Osoba, ktorá pridala toto stretnutie, môže skúsiť spracovanie znova.',
};

const sl: Copy = {
  overview: 'Povzetek',
  decisions: 'Odločitve',
  actionItems: 'Naloge',
  openQuestions: 'Odprta vprašanja',
  risks: 'Tveganja',
  ownerInferred: 'Odgovorna oseba: {name} (sklepano)',
  due: 'Rok {date}',
  dueText: 'Rok: {text}',
  dueInferred: 'Rok {date} (sklepano iz »{text}«)',
  notesGenerating: 'Pripravljanje zapiskov…',
  notesStale: 'Prepis ali govorci so se spremenili. Zapiski se osvežujejo…',
  notesCurrent: 'Zapiski so posodobljeni.',
  notesFailed: 'Zapiskov ni bilo mogoče pripraviti.',
  notesLegacyFailed: 'Prepis je varen, vendar je treba samodejne zapiske poskusiti znova.',
  notesNone: 'Zapiskov še ni.',
  retryNotes: 'Znova poskusi zapiske',
  regenerateNotes: 'Znova ustvari zapiske',
  notesRetryFailed: 'Zapiskov ni bilo mogoče znova zagnati.',
  transcriptRows: 'Prepis {rows}',
  showEvidence: 'Pokaži dokaz v prepisu',
  coveragePartial:
    'Zapiski zajemajo {included} od {total} segmentov prepisa. Preostanek prepisa preglejte neposredno.',
  suggestionsEyebrow: 'Preglejte pred shranjevanjem',
  suggestionsTitle: 'Možne posodobitve znanja podjetja',
  suggestionsBody: 'To so predlogi s sestanka. V znanje podjetja se nič ne doda samodejno.',
  reviewInChat: 'Preglej v klepetu',
  reviewInChatPrompt:
    'Primerjaj to točko s sestanka z našimi stranmi znanja in, če je trajna in točna, predlagaj spremembo prave strani v pregled: »{text}«',
  relatedContext: 'Povezan kontekst podjetja',
  stageCapturing: 'Snemanje',
  stageSealing: 'Shranjevanje posnetka',
  stageTranscribing: 'Prepisovanje posnetka',
  stageProcessing: 'Pripravljanje prepisa',
  stageReady: 'Pripravljeno',
  rowNotesGenerating: 'Pripravljeno · pripravljanje zapiskov',
  rowNotesStale: 'Pripravljeno · osveževanje zapiskov',
  rowNotesFailed: 'Pripravljeno · zapiski potrebujejo pozornost',
  rowFailed: 'Potrebuje pozornost',
  processingEyebrow: 'Obdelava sestanka',
  processingNote:
    'Ta pogled lahko zaprete. Obdelava se nadaljuje v ozadju, izvirnik pa ostane na voljo.',
  stageFailedSealing: 'Posnetka ni bilo mogoče shraniti. Zvok je ohranjen.',
  stageFailedTranscribing: 'Posnetka ni bilo mogoče prepisati. Izvirnik je ohranjen.',
  stageFailedOther: 'Izvirni posnetek je varen, vendar obdelave ni bilo mogoče dokončati.',
  failedEyebrow: 'Sestanek potrebuje pozornost',
  retry: 'Poskusi znova',
  retryFailed: 'Sestanka ni bilo mogoče znova obdelati.',
  canvasEyebrow: 'Sestanek · Izvirnik ohranjen',
  noAttendees: 'Ni dodanih udeležencev',
  paymentRequired:
    'Kredit za uporabo vašega podjetja je porabljen. Skrbnik lahko doda kartico, nato poskusite znova.',
  failureDetails: 'Podrobnosti',
  retryOwnerOnly: 'Oseba, ki je dodala ta sestanek, lahko znova poskusi z obdelavo.',
};

const hr: Copy = {
  overview: 'Sažetak',
  decisions: 'Odluke',
  actionItems: 'Zadaci',
  openQuestions: 'Otvorena pitanja',
  risks: 'Rizici',
  ownerInferred: 'Odgovorna osoba: {name} (zaključeno)',
  due: 'Rok {date}',
  dueText: 'Rok: {text}',
  dueInferred: 'Rok {date} (zaključeno iz „{text}”)',
  notesGenerating: 'Priprema bilješki…',
  notesStale: 'Transkript ili govornici su se promijenili. Bilješke se osvježavaju…',
  notesCurrent: 'Bilješke su ažurne.',
  notesFailed: 'Bilješke nije bilo moguće pripremiti.',
  notesLegacyFailed: 'Transkript je siguran, ali automatske bilješke treba ponovno pokušati.',
  notesNone: 'Još nema bilješki.',
  retryNotes: 'Ponovno pokušaj bilješke',
  regenerateNotes: 'Ponovno izradi bilješke',
  notesRetryFailed: 'Bilješke nije bilo moguće ponovno pokrenuti.',
  transcriptRows: 'Transkript {rows}',
  showEvidence: 'Prikaži dokaz u transkriptu',
  coveragePartial:
    'Bilješke obuhvaćaju {included} od {total} segmenata transkripta. Ostatak transkripta pregledajte izravno.',
  suggestionsEyebrow: 'Pregledajte prije spremanja',
  suggestionsTitle: 'Moguća ažuriranja znanja tvrtke',
  suggestionsBody: 'Ovo su prijedlozi sa sastanka. Ništa se ne dodaje u znanje tvrtke automatski.',
  reviewInChat: 'Pregledaj u razgovoru',
  reviewInChatPrompt:
    'Usporedi ovu točku sa sastanka s našim stranicama znanja i, ako je trajna i točna, predloži izmjenu prave stranice na pregled: „{text}”',
  relatedContext: 'Povezani kontekst tvrtke',
  stageCapturing: 'Snimanje',
  stageSealing: 'Spremanje snimke',
  stageTranscribing: 'Transkripcija snimke',
  stageProcessing: 'Priprema transkripta',
  stageReady: 'Spremno',
  rowNotesGenerating: 'Spremno · priprema bilješki',
  rowNotesStale: 'Spremno · osvježavanje bilješki',
  rowNotesFailed: 'Spremno · bilješke traže pažnju',
  rowFailed: 'Traži pažnju',
  processingEyebrow: 'Obrada sastanka',
  processingNote:
    'Možete zatvoriti ovaj prikaz. Obrada se nastavlja u pozadini, a izvornik ostaje dostupan.',
  stageFailedSealing: 'Snimku nije bilo moguće spremiti. Zvuk je sačuvan.',
  stageFailedTranscribing: 'Snimku nije bilo moguće transkribirati. Izvornik je sačuvan.',
  stageFailedOther: 'Izvorna snimka je sigurna, ali obradu nije bilo moguće dovršiti.',
  failedEyebrow: 'Sastanak traži pažnju',
  retry: 'Pokušaj ponovno',
  retryFailed: 'Sastanak nije bilo moguće ponovno obraditi.',
  canvasEyebrow: 'Sastanak · Izvornik sačuvan',
  noAttendees: 'Nema dodanih sudionika',
  paymentRequired:
    'Kredit za korištenje vaše tvrtke je potrošen. Administrator može dodati karticu, a zatim pokušajte ponovno.',
  failureDetails: 'Pojedinosti',
  retryOwnerOnly: 'Osoba koja je dodala ovaj sastanak može ponovno pokušati obradu.',
};

const bg: Copy = {
  overview: 'Обобщение',
  decisions: 'Решения',
  actionItems: 'Задачи',
  openQuestions: 'Отворени въпроси',
  risks: 'Рискове',
  ownerInferred: 'Отговорник: {name} (изведено)',
  due: 'Срок {date}',
  dueText: 'Срок: {text}',
  dueInferred: 'Срок {date} (изведено от „{text}“)',
  notesGenerating: 'Подготвят се бележки…',
  notesStale: 'Транскрипцията или говорещите се промениха. Бележките се обновяват…',
  notesCurrent: 'Бележките са актуални.',
  notesFailed: 'Бележките не можаха да бъдат подготвени.',
  notesLegacyFailed:
    'Транскрипцията е запазена, но автоматичните бележки трябва да се опитат отново.',
  notesNone: 'Все още няма бележки.',
  retryNotes: 'Опитай бележките отново',
  regenerateNotes: 'Създай бележките отново',
  notesRetryFailed: 'Бележките не можаха да бъдат стартирани отново.',
  transcriptRows: 'Транскрипция {rows}',
  showEvidence: 'Покажи доказателството в транскрипцията',
  coveragePartial:
    'Бележките обхващат {included} от {total} сегмента на транскрипцията. Прегледайте останалата част директно.',
  suggestionsEyebrow: 'Прегледайте преди запазване',
  suggestionsTitle: 'Възможни актуализации на фирменото знание',
  suggestionsBody:
    'Това са предложения от срещата. Нищо не се добавя автоматично към фирменото знание.',
  reviewInChat: 'Прегледай в чата',
  reviewInChatPrompt:
    'Сравни тази точка от срещата с нашите страници със знания и, ако е трайна и точна, предложи промяна на правилната страница за преглед: „{text}“',
  relatedContext: 'Свързан фирмен контекст',
  stageCapturing: 'Записва се',
  stageSealing: 'Записът се запазва',
  stageTranscribing: 'Записът се транскрибира',
  stageProcessing: 'Подготвя се транскрипцията',
  stageReady: 'Готово',
  rowNotesGenerating: 'Готово · подготвят се бележки',
  rowNotesStale: 'Готово · обновяват се бележки',
  rowNotesFailed: 'Готово · бележките изискват внимание',
  rowFailed: 'Изисква внимание',
  processingEyebrow: 'Срещата се обработва',
  processingNote:
    'Можете да затворите този изглед. Обработката продължава във фонов режим и оригиналът остава достъпен.',
  stageFailedSealing: 'Записът не можа да бъде запазен. Звукът е съхранен.',
  stageFailedTranscribing: 'Записът не можа да бъде транскрибиран. Оригиналът е съхранен.',
  stageFailedOther: 'Оригиналният запис е в безопасност, но обработката не можа да завърши.',
  failedEyebrow: 'Срещата изисква внимание',
  retry: 'Опитай отново',
  retryFailed: 'Срещата не можа да бъде обработена отново.',
  canvasEyebrow: 'Среща · Оригиналът е запазен',
  noAttendees: 'Няма добавени участници',
  paymentRequired:
    'Кредитът за използване на фирмата ви е изчерпан. Администратор може да добави карта, след което опитайте отново.',
  failureDetails: 'Подробности',
  retryOwnerOnly: 'Човекът, който е добавил тази среща, може да опита обработката отново.',
};

const el: Copy = {
  overview: 'Σύνοψη',
  decisions: 'Αποφάσεις',
  actionItems: 'Ενέργειες',
  openQuestions: 'Ανοιχτά ερωτήματα',
  risks: 'Κίνδυνοι',
  ownerInferred: 'Υπεύθυνος: {name} (συμπέρασμα)',
  due: 'Προθεσμία {date}',
  dueText: 'Προθεσμία: {text}',
  dueInferred: 'Προθεσμία {date} (συμπέρασμα από «{text}»)',
  notesGenerating: 'Προετοιμασία σημειώσεων…',
  notesStale: 'Η απομαγνητοφώνηση ή οι ομιλητές άλλαξαν. Οι σημειώσεις ανανεώνονται…',
  notesCurrent: 'Οι σημειώσεις είναι ενημερωμένες.',
  notesFailed: 'Δεν ήταν δυνατή η προετοιμασία των σημειώσεων.',
  notesLegacyFailed:
    'Η απομαγνητοφώνηση είναι ασφαλής, αλλά οι αυτόματες σημειώσεις χρειάζονται νέα προσπάθεια.',
  notesNone: 'Δεν υπάρχουν ακόμη σημειώσεις.',
  retryNotes: 'Επανάληψη σημειώσεων',
  regenerateNotes: 'Νέα δημιουργία σημειώσεων',
  notesRetryFailed: 'Δεν ήταν δυνατή η επανεκκίνηση των σημειώσεων.',
  transcriptRows: 'Απομαγνητοφώνηση {rows}',
  showEvidence: 'Εμφάνιση τεκμηρίου στην απομαγνητοφώνηση',
  coveragePartial:
    'Οι σημειώσεις καλύπτουν {included} από {total} τμήματα της απομαγνητοφώνησης. Δείτε απευθείας το υπόλοιπο.',
  suggestionsEyebrow: 'Έλεγχος πριν από την αποθήκευση',
  suggestionsTitle: 'Πιθανές ενημερώσεις της εταιρικής γνώσης',
  suggestionsBody:
    'Αυτές είναι προτάσεις από τη σύσκεψη. Τίποτα δεν προστίθεται αυτόματα στην εταιρική γνώση.',
  reviewInChat: 'Έλεγχος στη συνομιλία',
  reviewInChatPrompt:
    'Σύγκρινε αυτό το σημείο της σύσκεψης με τις σελίδες γνώσης μας και, αν είναι διαρκές και ακριβές, πρότεινε μια αλλαγή στη σωστή σελίδα για έλεγχο: «{text}»',
  relatedContext: 'Σχετικό εταιρικό πλαίσιο',
  stageCapturing: 'Εγγραφή σε εξέλιξη',
  stageSealing: 'Αποθήκευση της εγγραφής',
  stageTranscribing: 'Απομαγνητοφώνηση της εγγραφής',
  stageProcessing: 'Προετοιμασία της απομαγνητοφώνησης',
  stageReady: 'Έτοιμη',
  rowNotesGenerating: 'Έτοιμη · προετοιμασία σημειώσεων',
  rowNotesStale: 'Έτοιμη · ανανέωση σημειώσεων',
  rowNotesFailed: 'Έτοιμη · οι σημειώσεις χρειάζονται προσοχή',
  rowFailed: 'Χρειάζεται προσοχή',
  processingEyebrow: 'Επεξεργασία σύσκεψης',
  processingNote:
    'Μπορείτε να κλείσετε αυτή την προβολή. Η επεξεργασία συνεχίζεται στο παρασκήνιο και το πρωτότυπο παραμένει διαθέσιμο.',
  stageFailedSealing: 'Δεν ήταν δυνατή η αποθήκευση της εγγραφής. Ο ήχος διατηρείται.',
  stageFailedTranscribing:
    'Δεν ήταν δυνατή η απομαγνητοφώνηση της εγγραφής. Το πρωτότυπο διατηρείται.',
  stageFailedOther:
    'Η αρχική εγγραφή είναι ασφαλής, αλλά η επεξεργασία δεν μπόρεσε να ολοκληρωθεί.',
  failedEyebrow: 'Η σύσκεψη χρειάζεται προσοχή',
  retry: 'Δοκιμάστε ξανά',
  retryFailed: 'Δεν ήταν δυνατή η νέα επεξεργασία της σύσκεψης.',
  canvasEyebrow: 'Σύσκεψη · Το πρωτότυπο διατηρείται',
  noAttendees: 'Δεν προστέθηκαν συμμετέχοντες',
  paymentRequired:
    'Η πίστωση χρήσης της εταιρείας σας εξαντλήθηκε. Ένας διαχειριστής μπορεί να προσθέσει κάρτα· στη συνέχεια δοκιμάστε ξανά.',
  failureDetails: 'Λεπτομέρειες',
  retryOwnerOnly: 'Το άτομο που πρόσθεσε αυτή τη σύσκεψη μπορεί να δοκιμάσει ξανά την επεξεργασία.',
};

const hu: Copy = {
  overview: 'Összefoglaló',
  decisions: 'Döntések',
  actionItems: 'Teendők',
  openQuestions: 'Nyitott kérdések',
  risks: 'Kockázatok',
  ownerInferred: 'Felelős: {name} (következtetett)',
  due: 'Határidő {date}',
  dueText: 'Határidő: {text}',
  dueInferred: 'Határidő {date} (következtetve ebből: „{text}”)',
  notesGenerating: 'Jegyzetek előkészítése…',
  notesStale: 'Az átirat vagy a beszélők megváltoztak. A jegyzetek frissülnek…',
  notesCurrent: 'A jegyzetek naprakészek.',
  notesFailed: 'A jegyzeteket nem sikerült elkészíteni.',
  notesLegacyFailed:
    'Az átirat biztonságban van, de az automatikus jegyzeteket újra meg kell próbálni.',
  notesNone: 'Még nincsenek jegyzetek.',
  retryNotes: 'Jegyzetek újrapróbálása',
  regenerateNotes: 'Jegyzetek újrakészítése',
  notesRetryFailed: 'A jegyzeteket nem sikerült újraindítani.',
  transcriptRows: 'Átirat {rows}',
  showEvidence: 'Bizonyíték megjelenítése az átiratban',
  coveragePartial:
    'A jegyzetek {total} átiratszakaszból {included} szakaszt fednek le. Az átirat többi részét közvetlenül nézze át.',
  suggestionsEyebrow: 'Mentés előtt ellenőrizze',
  suggestionsTitle: 'A céges tudás lehetséges frissítései',
  suggestionsBody:
    'Ezek a megbeszélésből származó javaslatok. Semmi sem kerül automatikusan a céges tudásba.',
  reviewInChat: 'Áttekintés a csevegésben',
  reviewInChatPrompt:
    'Vesd össze ezt a megbeszélésen elhangzott pontot a tudásoldalainkkal, és ha tartós és pontos, javasolj módosítást a megfelelő oldalon ellenőrzésre: „{text}”',
  relatedContext: 'Kapcsolódó céges kontextus',
  stageCapturing: 'Felvétel folyamatban',
  stageSealing: 'A felvétel mentése',
  stageTranscribing: 'A felvétel átírása',
  stageProcessing: 'Az átirat előkészítése',
  stageReady: 'Kész',
  rowNotesGenerating: 'Kész · jegyzetek előkészítése',
  rowNotesStale: 'Kész · jegyzetek frissítése',
  rowNotesFailed: 'Kész · a jegyzetek figyelmet igényelnek',
  rowFailed: 'Figyelmet igényel',
  processingEyebrow: 'A megbeszélés feldolgozása',
  processingNote:
    'Ezt a nézetet bezárhatja. A feldolgozás a háttérben folytatódik, az eredeti elérhető marad.',
  stageFailedSealing: 'A felvételt nem sikerült menteni. A hang megmarad.',
  stageFailedTranscribing: 'A felvételt nem sikerült átírni. Az eredeti megmarad.',
  stageFailedOther:
    'Az eredeti felvétel biztonságban van, de a feldolgozást nem sikerült befejezni.',
  failedEyebrow: 'A megbeszélés figyelmet igényel',
  retry: 'Újrapróbálás',
  retryFailed: 'A megbeszélést nem sikerült újra feldolgozni.',
  canvasEyebrow: 'Megbeszélés · Eredeti megőrizve',
  noAttendees: 'Nincsenek résztvevők hozzáadva',
  paymentRequired:
    'A cég felhasználási kerete elfogyott. Egy adminisztrátor hozzáadhat egy kártyát, majd próbálja újra.',
  failureDetails: 'Részletek',
  retryOwnerOnly: 'A megbeszélést hozzáadó személy újra megpróbálhatja a feldolgozást.',
};

const ro: Copy = {
  overview: 'Rezumat',
  decisions: 'Decizii',
  actionItems: 'Acțiuni',
  openQuestions: 'Întrebări deschise',
  risks: 'Riscuri',
  ownerInferred: 'Responsabil: {name} (dedus)',
  due: 'Termen {date}',
  dueText: 'Termen: {text}',
  dueInferred: 'Termen {date} (dedus din „{text}”)',
  notesGenerating: 'Se pregătesc notele…',
  notesStale: 'Transcrierea sau vorbitorii s-au schimbat. Notele se actualizează…',
  notesCurrent: 'Notele sunt actualizate.',
  notesFailed: 'Notele nu au putut fi pregătite.',
  notesLegacyFailed:
    'Transcrierea este în siguranță, dar notele automate trebuie încercate din nou.',
  notesNone: 'Încă nu există note.',
  retryNotes: 'Reîncearcă notele',
  regenerateNotes: 'Regenerează notele',
  notesRetryFailed: 'Notele nu au putut fi repornite.',
  transcriptRows: 'Transcriere {rows}',
  showEvidence: 'Arată dovada în transcriere',
  coveragePartial:
    'Notele acoperă {included} din {total} segmente ale transcrierii. Consultați direct restul transcrierii.',
  suggestionsEyebrow: 'Verificați înainte de salvare',
  suggestionsTitle: 'Posibile actualizări ale cunoștințelor companiei',
  suggestionsBody:
    'Acestea sunt sugestii din ședință. Nimic nu este adăugat automat la cunoștințele companiei.',
  reviewInChat: 'Analizează în chat',
  reviewInChatPrompt:
    'Compară acest punct din ședință cu paginile noastre de cunoștințe și, dacă este durabil și corect, sugerează o modificare a paginii potrivite pentru revizuire: „{text}”',
  relatedContext: 'Context al companiei asociat',
  stageCapturing: 'Se înregistrează',
  stageSealing: 'Se salvează înregistrarea',
  stageTranscribing: 'Se transcrie înregistrarea',
  stageProcessing: 'Se pregătește transcrierea',
  stageReady: 'Gata',
  rowNotesGenerating: 'Gata · se pregătesc notele',
  rowNotesStale: 'Gata · se actualizează notele',
  rowNotesFailed: 'Gata · notele necesită atenție',
  rowFailed: 'Necesită atenție',
  processingEyebrow: 'Ședința se procesează',
  processingNote:
    'Puteți închide această vizualizare. Procesarea continuă în fundal, iar originalul rămâne disponibil.',
  stageFailedSealing: 'Înregistrarea nu a putut fi salvată. Sunetul este păstrat.',
  stageFailedTranscribing: 'Înregistrarea nu a putut fi transcrisă. Originalul este păstrat.',
  stageFailedOther:
    'Înregistrarea originală este în siguranță, dar procesarea nu a putut fi finalizată.',
  failedEyebrow: 'Ședința necesită atenție',
  retry: 'Încearcă din nou',
  retryFailed: 'Ședința nu a putut fi procesată din nou.',
  canvasEyebrow: 'Ședință · Original păstrat',
  noAttendees: 'Nu au fost adăugați participanți',
  paymentRequired:
    'Creditul de utilizare al companiei tale s-a epuizat. Un administrator poate adăuga un card, apoi încearcă din nou.',
  failureDetails: 'Detalii',
  retryOwnerOnly: 'Persoana care a adăugat această ședință poate încerca din nou procesarea.',
};

const ga: Copy = {
  overview: 'Achoimre',
  decisions: 'Cinntí',
  actionItems: 'Gníomhartha',
  openQuestions: 'Ceisteanna oscailte',
  risks: 'Rioscaí',
  ownerInferred: 'Freagrach: {name} (tátal)',
  due: 'Dlite {date}',
  dueText: 'Dlite: {text}',
  dueInferred: 'Dlite {date} (tátal as “{text}”)',
  notesGenerating: 'Nótaí á n-ullmhú…',
  notesStale: 'Athraíodh an tras-scríbhinn nó na cainteoirí. Nótaí á n-athnuachan…',
  notesCurrent: 'Tá na nótaí cothrom le dáta.',
  notesFailed: 'Níorbh fhéidir na nótaí a ullmhú.',
  notesLegacyFailed:
    'Tá an tras-scríbhinn slán, ach ní mór triail eile a bhaint as na nótaí uathoibríocha.',
  notesNone: 'Níl aon nótaí fós.',
  retryNotes: 'Bain triail eile as na nótaí',
  regenerateNotes: 'Athghin na nótaí',
  notesRetryFailed: 'Níorbh fhéidir na nótaí a atosú.',
  transcriptRows: 'Tras-scríbhinn {rows}',
  showEvidence: 'Taispeáin an fhianaise sa tras-scríbhinn',
  coveragePartial:
    'Clúdaíonn na nótaí {included} as {total} deighleog den tras-scríbhinn. Léigh an chuid eile den tras-scríbhinn go díreach.',
  suggestionsEyebrow: 'Athbhreithnigh sula sábhálann tú',
  suggestionsTitle: 'Nuashonruithe féideartha ar eolas na cuideachta',
  suggestionsBody:
    'Is moltaí ón gcruinniú iad seo. Ní chuirtear aon rud le heolas na cuideachta go huathoibríoch.',
  reviewInChat: 'Athbhreithnigh sa chomhrá',
  reviewInChatPrompt:
    'Cuir an pointe seo ón gcruinniú i gcomparáid lenár leathanaigh eolais agus, má tá sé buan agus cruinn, mol athrú ar an leathanach ceart lena athbhreithniú: “{text}”',
  relatedContext: 'Comhthéacs gaolmhar na cuideachta',
  stageCapturing: 'Ag taifeadadh',
  stageSealing: 'An taifeadadh á shábháil',
  stageTranscribing: 'An taifeadadh á thras-scríobh',
  stageProcessing: 'An tras-scríbhinn á hullmhú',
  stageReady: 'Réidh',
  rowNotesGenerating: 'Réidh · nótaí á n-ullmhú',
  rowNotesStale: 'Réidh · nótaí á n-athnuachan',
  rowNotesFailed: 'Réidh · tá aird ag teastáil ó na nótaí',
  rowFailed: 'Aird ag teastáil',
  processingEyebrow: 'An cruinniú á phróiseáil',
  processingNote:
    'Is féidir leat an t-amharc seo a dhúnadh. Leanann an phróiseáil ar aghaidh sa chúlra agus fanann an bunleagan ar fáil.',
  stageFailedSealing: 'Níorbh fhéidir an taifeadadh a shábháil. Coinnítear an fhuaim.',
  stageFailedTranscribing: 'Níorbh fhéidir an taifeadadh a thras-scríobh. Coinnítear an bunleagan.',
  stageFailedOther: 'Tá an bunthaifeadadh slán, ach níorbh fhéidir an phróiseáil a chríochnú.',
  failedEyebrow: 'Tá aird ag teastáil ón gcruinniú',
  retry: 'Bain triail eile as',
  retryFailed: 'Níorbh fhéidir an cruinniú a phróiseáil arís.',
  canvasEyebrow: 'Cruinniú · Bunleagan coinnithe',
  noAttendees: 'Níor cuireadh aon rannpháirtí leis',
  paymentRequired:
    'Tá creidmheas úsáide do chuideachta ídithe. Is féidir le riarthóir cárta a chur leis, agus ansin bain triail eile as.',
  failureDetails: 'Sonraí',
  retryOwnerOnly:
    'Is féidir leis an duine a chuir an cruinniú seo leis an phróiseáil a thriail arís.',
};

const mt: Copy = {
  overview: 'Sommarju',
  decisions: 'Deċiżjonijiet',
  actionItems: 'Azzjonijiet',
  openQuestions: 'Mistoqsijiet miftuħa',
  risks: 'Riskji',
  ownerInferred: 'Responsabbli: {name} (dedott)',
  due: 'Skadenza {date}',
  dueText: 'Skadenza: {text}',
  dueInferred: 'Skadenza {date} (dedotta minn “{text}”)',
  notesGenerating: 'In-noti qed jitħejjew…',
  notesStale: 'It-traskrizzjoni jew il-kelliema nbidlu. In-noti qed jiġu aġġornati…',
  notesCurrent: 'In-noti huma aġġornati.',
  notesFailed: 'In-noti ma setgħux jitħejjew.',
  notesLegacyFailed:
    'It-traskrizzjoni hija sigura, iżda n-noti awtomatiċi jeħtieġu tentattiv ieħor.',
  notesNone: 'Għad m’hemmx noti.',
  retryNotes: 'Erġa’ pprova n-noti',
  regenerateNotes: 'Erġa’ oħloq in-noti',
  notesRetryFailed: 'In-noti ma setgħux jerġgħu jinbdew.',
  transcriptRows: 'Traskrizzjoni {rows}',
  showEvidence: 'Uri l-evidenza fit-traskrizzjoni',
  coveragePartial:
    'In-noti jkopru {included} minn {total} segmenti tat-traskrizzjoni. Aqra l-bqija tat-traskrizzjoni direttament.',
  suggestionsEyebrow: 'Irrevedi qabel ma tissejvja',
  suggestionsTitle: 'Aġġornamenti possibbli tal-għarfien tal-kumpanija',
  suggestionsBody:
    'Dawn huma suġġerimenti mil-laqgħa. Xejn ma jiżdied awtomatikament mal-għarfien tal-kumpanija.',
  reviewInChat: 'Irrevedi fiċ-chat',
  reviewInChatPrompt:
    'Qabbel dan il-punt mil-laqgħa mal-paġni tal-għarfien tagħna u, jekk huwa dejjiemi u preċiż, issuġġerixxi bidla fil-paġna t-tajba għar-reviżjoni: “{text}”',
  relatedContext: 'Kuntest relatat tal-kumpanija',
  stageCapturing: 'Qed jirrekordja',
  stageSealing: 'Ir-reġistrazzjoni qed tiġi ssejvjata',
  stageTranscribing: 'Ir-reġistrazzjoni qed tiġi traskritta',
  stageProcessing: 'It-traskrizzjoni qed titħejja',
  stageReady: 'Lesta',
  rowNotesGenerating: 'Lesta · in-noti qed jitħejjew',
  rowNotesStale: 'Lesta · in-noti qed jiġu aġġornati',
  rowNotesFailed: 'Lesta · in-noti jeħtieġu attenzjoni',
  rowFailed: 'Teħtieġ attenzjoni',
  processingEyebrow: 'Il-laqgħa qed tiġi pproċessata',
  processingNote:
    'Tista’ tagħlaq din il-veduta. L-ipproċessar ikompli fl-isfond u l-oriġinal jibqa’ disponibbli.',
  stageFailedSealing: 'Ir-reġistrazzjoni ma setgħetx tiġi ssejvjata. L-awdjo jinżamm.',
  stageFailedTranscribing: 'Ir-reġistrazzjoni ma setgħetx tiġi traskritta. L-oriġinal jinżamm.',
  stageFailedOther:
    'Ir-reġistrazzjoni oriġinali hija sigura, iżda l-ipproċessar ma setax jitlesta.',
  failedEyebrow: 'Il-laqgħa teħtieġ attenzjoni',
  retry: 'Erġa’ pprova',
  retryFailed: 'Il-laqgħa ma setgħetx terġa’ tiġi pproċessata.',
  canvasEyebrow: 'Laqgħa · Oriġinal miżmum',
  noAttendees: 'Ma ġie miżjud l-ebda parteċipant',
  paymentRequired:
    'Il-kreditu tal-użu tal-kumpanija tiegħek spiċċa. Amministratur jista’ jżid karta, imbagħad erġa’ pprova.',
  failureDetails: 'Dettalji',
  retryOwnerOnly: 'Il-persuna li żiedet din il-laqgħa tista’ terġa’ tipprova l-ipproċessar.',
};

const meetingsNotesCopy: NamespaceCatalog<MeetingsNotesCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default meetingsNotesCopy;
